import { useEffect, useMemo, useState } from "react";
import { Check, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";

const AR = { "أ": "ا", "إ": "ا", "آ": "ا", "ٱ": "ا", "ة": "ه", "ى": "ي", "ؤ": "و", "ئ": "ي", "گ": "ك", "پ": "ب", "ڤ": "ف", "ـ": "" };
const HE = { "ך": "כ", "ם": "מ", "ן": "נ", "ף": "פ", "ץ": "צ" };

export const norm = (s) => String(s || "").toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "")
  .replace(/[\u0600-\u06ff\u05d0-\u05ea]/g, (c) => AR[c] ?? HE[c] ?? c).replace(/[^0-9a-z\u0590-\u05ff\u0600-\u06ff]/g, "");

let cache = null;
const useBrands = () => {
  const [items, setItems] = useState(cache || []);
  useEffect(() => {
    if (!cache) api.get("/public/brands").then((r) => { cache = r.data.items; setItems(cache); }).catch(() => {});
  }, []);
  return items;
};

export const matchBrands = (brands, q) => {
  const k = norm(q);
  if (!k) return { exact: null, list: brands.slice(0, 8) };
  const exact = brands.find((b) => b.keys.includes(k)) || null;
  const scored = brands.map((b) => ({ b, s: b.keys.some((x) => x.startsWith(k)) ? 2 : b.keys.some((x) => x.includes(k)) ? 1 : 0 })).filter((x) => x.s);
  return { exact, list: scored.sort((a, b) => b.s - a.s).map((x) => x.b).slice(0, 8) };
};

export const BrandPicker = ({ value, onChange }) => {
  const { t } = useLang();
  const brands = useBrands();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  useEffect(() => { setQ(value || ""); }, [value]);
  const { exact, list } = useMemo(() => matchBrands(brands, q), [brands, q]);
  const pick = (name) => { setQ(name); onChange(name); setOpen(false); };
  const blur = () => setTimeout(() => { setOpen(false); if (exact && exact.name !== q) pick(exact.name); else onChange(q.trim()); }, 150);
  return (
    <div className="relative">
      <Input required autoComplete="off" className="xd-input" value={q} placeholder={t("spec.brand_placeholder")}
        onFocus={() => setOpen(true)} onBlur={blur} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }} onChange={(e) => { setQ(e.target.value); setOpen(true); }} data-testid="spec-brand-input" />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-[#0b0b0b] border border-white/15 shadow-2xl" data-testid="brand-suggestions">
          {list.map((b) => (
            <button type="button" key={b.name} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(b.name)}
              className="w-full px-3 py-2.5 text-start text-sm hover:bg-white/5 flex items-center justify-between gap-3" data-testid={`brand-suggestion-${b.name}`}>
              <span className="min-w-0 truncate"><span dir="ltr" className="text-white">{b.name}</span><span className="text-zinc-500 text-xs ms-2">{b.he !== b.name ? `${b.he} · ${b.ar}` : ""}</span></span>
              {exact?.name === b.name && <Check size={14} className="text-emerald-400 shrink-0" />}
            </button>
          ))}
          {q.trim() && !exact && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(q.trim())}
              className="w-full px-3 py-2.5 text-start text-sm text-zinc-300 hover:bg-white/5 border-t border-white/10 flex items-center gap-2" data-testid="brand-manual-option">
              <Plus size={14} className="shrink-0" />{t("spec.brand_manual", { b: q.trim() })}
            </button>
          )}
          {q.trim() && !exact && list.length > 0 && <p className="px-3 py-2 text-[11px] text-amber-300 border-t border-white/5" data-testid="brand-duplicate-hint">{t("spec.brand_check")}</p>}
        </div>
      )}
    </div>
  );
};
